import { EPlatform } from '@enums/platform.enum';
import backendHttpClient from '@services/_httpClient/backend.http-client';

import type { AddServiceRequestDocumentsPayload } from './dtos/addDocuments.dto';
import type { CreateServiceRequestPayload } from './dtos/create.dto';
import type { GetAllServiceRequestsParams } from './dtos/getAll.dto';
import type {
  UpdateServiceRequestDto,
  UpdateServiceRequestPayload,
} from './dtos/update.dto';
import type { ServiceRequestRepository } from './repository';
import type {
  AddServiceRequestDocumentsOutput,
  AddServiceRequestDocumentsResponse,
} from './types/addDocuments.type';
import type {
  CreateServiceRequestOutput,
  CreateServiceRequestResponse,
} from './types/create.type';
import type {
  GetAllServiceRequestsOutput,
  GetAllServiceRequestsResponse,
} from './types/getAll.type';
import type {
  GetAllServiceRequestDocumentsOutput,
  GetAllServiceRequestDocumentsParams,
  GetAllServiceRequestDocumentsQueryParams,
  GetAllServiceRequestDocumentsResponse,
} from './types/getAllDocuments.type';
import type {
  GetAllServiceRequestInterventionsOutput,
  GetAllServiceRequestInterventionsParams,
  GetAllServiceRequestInterventionsQueryParams,
  GetAllServiceRequestInterventionsResponse,
} from './types/getAllInterventions.type';
import type {
  GetAllServiceRequestNotesOutput,
  GetAllServiceRequestNotesParams,
  GetAllServiceRequestNotesQueryParams,
  GetAllServiceRequestNotesResponse,
} from './types/getAllNotes.type';
import type {
  GetServiceRequestByIdOutput,
  GetServiceRequestByIdResponse,
} from './types/getById.type';
import type {
  GetServiceRequestTimelineOutput,
  GetServiceRequestTimelineResponse,
} from './types/getTimeline.type';

class ServiceRequestService implements ServiceRequestRepository {
  public async create(
    payload: CreateServiceRequestPayload,
  ): Promise<CreateServiceRequestOutput> {
    const { data } = await backendHttpClient.post<
      CreateServiceRequestResponse,
      CreateServiceRequestPayload
    >({ path: 'service-requests', body: payload });

    return {
      id: data.serviceRequest.id,
    };
  }

  public async getAll(
    params: GetAllServiceRequestsParams,
  ): Promise<GetAllServiceRequestsOutput> {
    const { data } = await backendHttpClient.get<
      GetAllServiceRequestsResponse,
      GetAllServiceRequestsParams
    >({
      path: 'service-requests',
      params: {
        page: params.page,
        perPage: params.perPage,
        status: params.status,
      },
    });

    return {
      count: data.count,
      list: data.list,
      pagination: data.pagination,
    };
  }

  public async getById(id: string): Promise<GetServiceRequestByIdOutput> {
    const { data } = await backendHttpClient.get<GetServiceRequestByIdResponse>(
      {
        path: `service-requests/${id}`,
      },
    );

    return { serviceRequest: data.serviceRequest };
  }

  public async update(payload: UpdateServiceRequestPayload): Promise<void> {
    await backendHttpClient.put<void, UpdateServiceRequestDto>({
      path: `service-requests/${payload.id}`,
      body: payload,
    });
  }

  public async getAllInterventions(
    params: GetAllServiceRequestInterventionsParams,
  ): Promise<GetAllServiceRequestInterventionsOutput> {
    const { data } = await backendHttpClient.get<
      GetAllServiceRequestInterventionsResponse,
      GetAllServiceRequestInterventionsQueryParams
    >({
      path: `service-requests/${params.id}/interventions`,
      params: {
        page: params.page,
        perPage: params.perPage,
        platform: EPlatform.Connect,
      },
    });

    return {
      list: data.list,
      pagination: data.pagination,
    };
  }

  public async getAllNotes(
    params: GetAllServiceRequestNotesParams,
  ): Promise<GetAllServiceRequestNotesOutput> {
    const { data } = await backendHttpClient.get<
      GetAllServiceRequestNotesResponse,
      GetAllServiceRequestNotesQueryParams
    >({
      path: `service-requests/${params.id}/notes`,
      params: {
        page: params.page,
        perPage: params.perPage,
        platform: EPlatform.Connect,
      },
    });

    return {
      list: data.list,
      pagination: data.pagination,
    };
  }

  public async getTimeline(
    id: string,
  ): Promise<GetServiceRequestTimelineOutput> {
    const { data } =
      await backendHttpClient.get<GetServiceRequestTimelineResponse>({
        path: `service-requests/${id}/timeline`,
      });

    return {
      timeline: data.timeline,
    };
  }

  public async addDocuments(
    payload: AddServiceRequestDocumentsPayload,
  ): Promise<AddServiceRequestDocumentsOutput> {
    const formData = new FormData();

    payload.documents.forEach(document => {
      formData.append('file', document.file);
    });

    formData.append(
      'metadata',
      JSON.stringify(
        payload.documents.map(doc => ({
          fileName: doc.file.name,
          type: doc.type,
        })),
      ),
    );

    const { data } = await backendHttpClient.post<
      AddServiceRequestDocumentsResponse,
      FormData
    >({
      path: `service-requests/${payload.id}/documents`,
      body: formData,
    });

    return {
      uploaded: data.list,
    };
  }

  public async getAllDocuments(
    params: GetAllServiceRequestDocumentsParams,
  ): Promise<GetAllServiceRequestDocumentsOutput> {
    const { data } = await backendHttpClient.get<
      GetAllServiceRequestDocumentsResponse,
      GetAllServiceRequestDocumentsQueryParams
    >({
      path: `service-requests/${params.id}/documents`,
      params: {
        types: params.types,
        includeInterventionDocuments: params.includeInterventionDocuments,
      },
    });

    return {
      list: data.list,
      interventions: data.interventions,
    };
  }
}

export const serviceRequestService = new ServiceRequestService();
